/**
 * One template from the Templates table: its share and allocated cost, who
 * raised its requests, and how they spread over the period.
 *
 * A table answers "which template is busiest"; the next questions are "who is
 * using it" and "what does that look like", and a full page navigation to
 * answer them costs the reader their place, their sort order and their period.
 *
 * "Raised by" is the template's row of the request grid (/billing/request-
 * breakdown), counted over the same requests and window as the table, so its
 * people add up to the Requests figure above it.
 */
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import DrawerShell, { DrawerStats } from './DrawerShell'
import BreakdownList from './BreakdownList'
import { bucketSeries, formatInt, formatUsdAs } from '../../utils/billing'
import { raisedBy } from '../../utils/requestBreakdown'
import { isAllTimeLabel } from './useBillingData'

export default function DetailDrawer({ row, dates, chart, periodLabel, onClose, displayCurrency, breakdown = null, onRetryBreakdown }) {
  const open = Boolean(row)
  const people = open ? raisedBy(breakdown?.data, row.template_id) : { total: 0, rows: [] }

  // Over a long period the daily bars become one-pixel noise, so they are
  // summed into wider buckets — and relabelled, because a bar that is no
  // longer a day must not still be called one.
  const { values: counts, size: bucketDays } = bucketSeries(row?.counts || [], 30)
  const max = Math.max(1, ...counts)
  const busiest = counts.indexOf(max)
  const unit = bucketDays === 1 ? 'day' : `${bucketDays} days`

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      ariaLabel={`${row?.template_name} details`}
      eyebrow="Template"
      title={row?.template_name}
      subtitle={isAllTimeLabel(periodLabel) ? 'All time' : `Last ${periodLabel}`}
      footer={row?.template_id ? (
        <Link
          to={`/templates/${row.template_id}`}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy-700 dark:text-navy-300
                     hover:gap-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          Open this template
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      ) : null}
    >
      {open && (
        <>
          <DrawerStats stats={[
            { label: 'Requests', value: formatInt(row.total) },
            { label: 'Share', value: `${row.sharePct ?? Math.round((row.share || 0) * 100)}%` },
            { label: 'Allocated', value: formatUsdAs(row.allocated || 0, displayCurrency) },
          ]} />

          {breakdown && (
            <BreakdownList
              title="Raised by"
              kind="people"
              rows={people.rows}
              total={people.total}
              expectedTotal={row.total}
              loading={breakdown.loading}
              error={breakdown.error}
              onRetry={onRetryBreakdown}
              emptyText="No requests on this template in the period."
            />
          )}

          <div>
            <h3 className="text-[12px] font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Requests per {unit}
            </h3>
            <div className="flex items-end gap-0.5 h-24">
              {counts.map((count, i) => (
                <div
                  key={i}
                  className="flex-1 min-w-[2px] rounded-t-sm transition-[height] duration-500 ease-out"
                  style={{
                    height: `${Math.max(2, (count / max) * 100)}%`,
                    backgroundColor: i === busiest ? chart.series[0] : `${chart.series[0]}66`,
                  }}
                  title={`${count} request${count === 1 ? '' : 's'} per ${unit}, bar ${i + 1} of ${counts.length}`}
                />
              ))}
            </div>
            {dates?.length > 0 && counts.length > 0 && (
              <div className="flex justify-between mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                <span>{dates[0]}</span>
                <span>{dates[dates.length - 1]}</span>
              </div>
            )}
            {counts.length > 0 && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                Busiest {unit === 'day' ? 'day' : 'period'} carried {formatInt(max)} requests.
              </p>
            )}
          </div>

          <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
            Request counts are measured, and each request is credited to whoever raised it. The allocated cost is this
            billing month&apos;s estimated AI processing charge split by request share; consumption is not recorded per
            template.
          </p>
        </>
      )}
    </DrawerShell>
  )
}
